import React, { useState, useEffect, useRef } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { Play, Bookmark, Trash2, ExternalLink } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import LeftMenu from "../components/LeftMenu";
import LeftShadow from "../components/LeftShadow";
import MobileMenu from "../components/MobileMenu";
import PlyrVideoPlayer from "../components/PlyrVideoPlayer";
import api from "../services/api";
import { debugLog } from "../utils/debugLog";
import webPlayerStorage from "../utils/webPlayerStorage";

const EMPTY_MESSAGES = [
  "Type like no one's watching... but we are. 👀⌨️",
  "Feed the search goblin. He's hungry. 🧌🔍",
  "Your keyboard called. It wants adventure. 🎒📺",
  "Looking for love in all the wrong genres? 💔🎞️",
  "Enter a title… or just slam your face on the keyboard. 🤕🎬",
  "Don't overthink it. We've got bad taste too. 😎🍿",
  "What's on your mind? No wrong answers (except Cats). 🐱🎤",
  "Type fast. The algorithm gets bored easily. 🧠💤",
  "Searching is free. Regret is forever. 🫣🎧",
  "Still here? Must be fate. Search your destiny. 🔮🎥",
];

const SEARCH_FILTERS = [
  { id: "all", label: "All", icon: "fa-layer-group" },
  { id: "movie", label: "Movies", icon: "fa-film" },
  { id: "tv", label: "Series", icon: "fa-tv" },
  { id: "live-tv", label: "Live TV", icon: "fa-bolt" },
  { id: "cast", label: "Cast", icon: "fa-user-group" },
];

const pillClass = (active) =>
  `flex h-12 shrink-0 items-center rounded-lg px-2.5 text-sm ${
    active
      ? "bg-white font-bold text-[#111111]"
      : "border border-[#1A1A1A] bg-white/[0.08] text-white"
  }`;

const textOf = (value) => {
  if (value == null) return "";
  if (typeof value === "object") return value.value || value.text || "";

  return String(value);
};

const WebPlayerSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, playlist } = location.state || {};
  const [searchQuery, setSearchQuery] = useState("");
  const [randomMessage, setRandomMessage] = useState("");
  const [, setSearchResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [totalItems, setTotalItems] = useState(0);
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const [resultsChrome, setResultsChrome] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentStreamUrl, setCurrentStreamUrl] = useState(null);
  const [currentStreamTitle, setCurrentStreamTitle] = useState(null);
  const [currentStreamPoster, setCurrentStreamPoster] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showHistoryOverlay, setShowHistoryOverlay] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [isItemBookmarked, setIsItemBookmarked] = useState(false);
  const searchInputRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const performSearchRef = useRef(null);
  const pageRef = useRef(1);
  const totalPagesRef = useRef(Infinity);
  const requestTokenRef = useRef(0);
  const isLoadingRef = useRef(false);
  const resultsRef = useRef([]);
  const signedIn = Boolean(webPlayerStorage.getToken());

  resultsRef.current = filteredResults;

  useEffect(() => {
    // Pick a random message on mount
    const randomIndex = Math.floor(Math.random() * EMPTY_MESSAGES.length);

    setRandomMessage(EMPTY_MESSAGES[randomIndex]);

    // Load search history items from localStorage
    const savedHistory = localStorage.getItem("searchHistoryItems");

    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);

        setSearchHistory(parsedHistory);
      } catch (error) {
        console.error("Error loading search history:", error);
      }
    }

    // Restore previous search state from sessionStorage (for back navigation)
    const savedSearchQuery = sessionStorage.getItem("lastSearchQuery");
    const savedSearchResults = sessionStorage.getItem("lastSearchResults");

    if (savedSearchQuery) {
      if (savedSearchResults) {
        try {
          const parsedResults = JSON.parse(savedSearchResults);

          if (parsedResults && parsedResults.length > 0) {
            setSearchResults(parsedResults);
            setFilteredResults(parsedResults);
            setSearchSubmitted(true);
            setResultsChrome(true);
            setTotalItems(parsedResults.length);
          }
        } catch (error) {
          console.error("Error restoring search results:", error);
        }
      }
      setSearchQuery(savedSearchQuery);
    }

    // Focus on search input
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (searchQuery) {
      sessionStorage.setItem("lastSearchQuery", searchQuery);
    }
  }, [searchQuery]);

  const addToSearchHistory = (item) => {
    if (!item) return;

    // Create a history item with essential data
    const historyItem = {
      id: item.mediaId || item.id,
      name: item.name || item.title,
      mediaType: item.mediaType || item.type,
      posterPath: item.posterPath,
      logoPath: item.logoPath,
      backdropPath: item.backdropPath,
      rating: item.rating,
      streamUrl: item.streamUrl,
      programmes: item.programmes,
      isBookmarked: item.isBookmarked,
    };

    // Create a new history array
    let newHistory = [...searchHistory];

    // Remove duplicate if it exists (by id and mediaType)
    newHistory = newHistory.filter(
      (h) => !(h.id === historyItem.id && h.mediaType === historyItem.mediaType)
    );

    // Add to the beginning
    newHistory.unshift(historyItem);

    if (newHistory.length > 10) {
      newHistory = newHistory.slice(0, 10);
    }

    // Save to state and localStorage
    setSearchHistory(newHistory);
    localStorage.setItem("searchHistoryItems", JSON.stringify(newHistory));
  };

  const removeFromSearchHistory = (item) => {
    const newHistory = searchHistory.filter(
      (h) => !(h.id === item.id && h.mediaType === item.mediaType)
    );

    setSearchHistory(newHistory);
    localStorage.setItem("searchHistoryItems", JSON.stringify(newHistory));
  };

  const clearAllSearchHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem("searchHistoryItems");
  };

  const handleHistoryItemClick = (item) => {
    const searchTerm = textOf(
      item.query || item.name || item.title
    ).toUpperCase();

    if (!searchTerm) return;

    setSearchQuery(searchTerm);
    setActiveFilter("all");
    performSearchRef.current?.(searchTerm, "all");
  };

  const handleHistoryLongPressStart = (item) => {
    longPressTimerRef.current = setTimeout(() => {
      setSelectedHistoryItem(item);
      setIsItemBookmarked(item.isBookmarked || false);
      setShowHistoryOverlay(true);
    }, 500); // 500ms for long press
  };

  const handleHistoryLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleOverlayAction = async (action) => {
    if (!selectedHistoryItem) return;

    const item = selectedHistoryItem;
    const isMovie = item.mediaType === "movie" || item.type === "movie";
    const isSeries =
      item.mediaType === "series" ||
      item.mediaType === "tv" ||
      item.type === "series";

    switch (action) {
      case "play":
        setShowHistoryOverlay(false);
        handleContentClick(item);
        break;
      case "goto":
        setShowHistoryOverlay(false);
        if (isMovie) {
          navigate(`/web-player/movie/${item.id}`);
        } else if (isSeries) {
          navigate(`/web-player/series/${item.id}`);
        }
        break;
      case "watchlist":
        try {
          if (isItemBookmarked) {
            await api.webPlayerDeleteBookmark(item.id, item.mediaType);
            setIsItemBookmarked(false);
            toast.success("Removed from favorites");
          } else {
            await api.webPlayerCreateBookmark(item.id, item.mediaType);
            setIsItemBookmarked(true);
            toast.success("Added to favorites");
          }
          // Update the item in history
          const updatedHistory = searchHistory.map((h) =>
            h.id === item.id && h.mediaType === item.mediaType
              ? { ...h, isBookmarked: !isItemBookmarked }
              : h
          );

          setSearchHistory(updatedHistory);
          localStorage.setItem(
            "searchHistoryItems",
            JSON.stringify(updatedHistory)
          );
        } catch (error) {
          toast.error("Failed to update favorites");
        }
        break;
      case "remove":
        removeFromSearchHistory(item);
        setShowHistoryOverlay(false);
        break;
      default:
        break;
    }
  };

  const getItemTypeLabel = (item) => {
    const isLiveTV =
      item.mediaType === "live-tv" ||
      item.mediaType === "livetv" ||
      item.type === "live-tv";
    const isMovie = item.mediaType === "movie" || item.type === "movie";

    if (isLiveTV) {
      return `Live TV [${item.rating || "NR"}]`;
    } else if (isMovie) {
      return `Movie [${item.rating || "NR"}]`;
    } else {
      return `Series [${item.rating || "NR"}]`;
    }
  };

  const getItemImage = (item) => {
    const isLiveTV =
      item.mediaType === "live-tv" ||
      item.mediaType === "livetv" ||
      item.type === "live-tv";

    if (isLiveTV) {
      // Priority: programme backdrop > channel backdrop > channel logo
      return (
        item.programmes?.[0]?.backdropPath ||
        item.backdropPath ||
        item.logoPath ||
        item.posterPath
      );
    }

    return item.posterPath || item.backdropPath || item.logoPath;
  };

  const performSearch = async (query, mediaType = "all", append = false) => {
    const term = query.trim();

    if (!term) return;
    if (
      append &&
      (isLoadingRef.current || pageRef.current > totalPagesRef.current)
    ) {
      return;
    }

    const page = append ? pageRef.current : 1;
    const requestToken = (requestTokenRef.current += 1);

    if (!append) {
      pageRef.current = 1;
      totalPagesRef.current = Infinity;
      setFilteredResults([]);
      setSearchResults([]);
      setTotalItems(0);
      if (mediaType !== "all") setResultsChrome(true);
    }

    try {
      isLoadingRef.current = true;
      setIsLoading(true);
      setSearchSubmitted(true);
      setActiveFilter(mediaType);
      const params = { term, limit: 45, page };

      if (mediaType !== "all") params.mediaType = mediaType;

      const response = await api.webPlayerSearch(params);

      if (requestToken !== requestTokenRef.current) return;

      if (response && response.data) {
        // Process and normalize the data structure
        if (response.data.length > 0) {
          response.data.forEach((item) => {
            // Handle rottenTomatoes - keep the full object structure
            if (item.rottenTomatoes) {
              // Store the original data
              const originalRT = item.rottenTomatoes;

              // If it's already a number, keep it as is
              if (typeof originalRT === "number") {
                // Already in simple format, nothing to do
              } else if (typeof originalRT === "object") {
                // Store full data for display if it has critics or audience scores
                if (originalRT.critics || originalRT.audience) {
                  item.rottenTomatoesFull = {
                    critics: originalRT.critics,
                    audience: originalRT.audience,
                  };

                  // Also store a simple number for ContentRow compatibility
                  let rtScore = null;

                  if (originalRT.critics?.score !== undefined) {
                    rtScore = originalRT.critics.score;
                  } else if (originalRT.audience?.score !== undefined) {
                    rtScore = originalRT.audience.score;
                  }

                  if (rtScore !== undefined && rtScore !== null) {
                    const numericScore =
                      typeof rtScore === "string"
                        ? parseFloat(rtScore)
                        : rtScore;

                    if (!isNaN(numericScore)) {
                      item.rottenTomatoes = numericScore;
                    }
                  }
                } else if (
                  originalRT.score !== undefined ||
                  originalRT.value !== undefined ||
                  originalRT.rating !== undefined
                ) {
                  // Handle other object formats
                  const rtScore =
                    originalRT.score || originalRT.value || originalRT.rating;
                  const numericScore =
                    typeof rtScore === "string" ? parseFloat(rtScore) : rtScore;

                  if (!isNaN(numericScore)) {
                    item.rottenTomatoes = numericScore;
                  }
                }
              }
            }

            // Handle imdbRating - normalize to match homepage format
            if (item.imdb_rating) {
              // Extract value if it's an object
              if (
                typeof item.imdb_rating === "object" &&
                !Array.isArray(item.imdb_rating)
              ) {
                const imdbValue =
                  item.imdb_rating.score ||
                  item.imdb_rating.value ||
                  item.imdb_rating.rating;

                if (
                  imdbValue !== undefined &&
                  imdbValue !== null &&
                  imdbValue !== ""
                ) {
                  item.imdbRating = imdbValue; // Use imdbRating (camelCase) to match homepage
                }
              } else {
                item.imdbRating = item.imdb_rating; // Copy to camelCase property
              }
              delete item.imdb_rating; // Remove the snake_case version
            }

            // Also check if imdbRating exists as an object already
            if (
              item.imdbRating &&
              typeof item.imdbRating === "object" &&
              !Array.isArray(item.imdbRating)
            ) {
              const imdbValue =
                item.imdbRating.score ||
                item.imdbRating.value ||
                item.imdbRating.rating;

              if (
                imdbValue !== undefined &&
                imdbValue !== null &&
                imdbValue !== ""
              ) {
                item.imdbRating = imdbValue;
              } else {
                delete item.imdbRating;
              }
            }
          });
        }

        const total = response.meta?.totalItems ?? response.data.length;
        const pages = response.meta?.totalPages ?? 1;
        const merged = append
          ? [...resultsRef.current, ...response.data]
          : response.data;

        setTotalItems(total);
        pageRef.current = page + 1;
        totalPagesRef.current = pages;
        resultsRef.current = merged;
        setSearchResults(merged);
        setFilteredResults(merged);
        sessionStorage.setItem("lastSearchResults", JSON.stringify(merged));

        if (!append) {
          setResultsChrome(response.data.length > 0 || mediaType !== "all");
        }

        if (response.data.length === 0 && !append) {
          const randomIndex = Math.floor(Math.random() * EMPTY_MESSAGES.length);

          setRandomMessage(EMPTY_MESSAGES[randomIndex]);
        }
      }
    } catch (error) {
      if (requestToken !== requestTokenRef.current) return;

      if (!append) {
        setSearchResults([]);
        setFilteredResults([]);
        setTotalItems(0);
        setResultsChrome(mediaType !== "all");
        const randomIndex = Math.floor(Math.random() * EMPTY_MESSAGES.length);

        setRandomMessage(EMPTY_MESSAGES[randomIndex]);
      }

      if (
        !String(error?.message || "")
          .toLowerCase()
          .includes("cancelled")
      ) {
        toast.error(`Error Searching Media: ${error.message || "failed"}`);
      }
    } finally {
      if (requestToken === requestTokenRef.current) {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  };

  performSearchRef.current = performSearch;

  useEffect(() => {
    const onScroll = () => {
      if (!resultsChrome || isLoadingRef.current) return;
      if (pageRef.current > totalPagesRef.current) return;

      const nearBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 600;

      if (nearBottom) {
        performSearchRef.current?.(searchQuery, activeFilter, true);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, [resultsChrome, searchQuery, activeFilter]);

  const handleSearchInput = (e) => {
    const value = e.target.value.toUpperCase();

    requestTokenRef.current += 1;
    isLoadingRef.current = false;
    setIsLoading(false);
    setSearchQuery(value);
    setActiveFilter("all");
    setSearchSubmitted(false);
    setResultsChrome(false);

    if (!value.trim()) {
      setSearchResults([]);
      setFilteredResults([]);
      setTotalItems(0);
      pageRef.current = 1;
      totalPagesRef.current = Infinity;
      sessionStorage.removeItem("lastSearchQuery");
      sessionStorage.removeItem("lastSearchResults");
    }
  };

  const clearSearch = () => {
    requestTokenRef.current += 1;
    isLoadingRef.current = false;
    pageRef.current = 1;
    totalPagesRef.current = Infinity;
    setSearchQuery("");
    setSearchResults([]);
    setFilteredResults([]);
    setTotalItems(0);
    setSearchSubmitted(false);
    setResultsChrome(false);
    setIsLoading(false);
    setActiveFilter("all");
    // Clear saved search state from sessionStorage
    sessionStorage.removeItem("lastSearchQuery");
    sessionStorage.removeItem("lastSearchResults");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Pick a new random message when clearing
    const randomIndex = Math.floor(Math.random() * EMPTY_MESSAGES.length);

    setRandomMessage(EMPTY_MESSAGES[randomIndex]);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      clearSearch();

      return;
    }

    if (e.key === "Enter") {
      const query = searchQuery.trim();

      if (!query) return;

      setActiveFilter("all");
      performSearch(query, "all");
    }
  };

  const selectFilter = (mediaType) => {
    setActiveFilter(mediaType);

    if (searchQuery.trim()) performSearch(searchQuery, mediaType);
  };

  const handleContentClick = (item) => {
    // Add to search history when an item is clicked
    addToSearchHistory(item);

    if (item.mediaType === "cast" || item.type === "cast") {
      const castId = item.mediaId || item.id;

      if (castId) navigate(`/web-player/cast/${castId}`);

      return;
    }

    // Handle Live TV stream playback
    if (
      (item.mediaType === "live-tv" ||
        item.type === "live-tv" ||
        item.type === "livetv") &&
      item.streamUrl
    ) {
      // Decode base64 stream URL
      let decodedUrl = item.streamUrl;

      try {
        // Check if it's base64 encoded (doesn't start with http)
        if (!item.streamUrl.startsWith("http")) {
          decodedUrl = atob(item.streamUrl);
        }
        debugLog("Selected channel:", item.name || item.title);
        debugLog("Decoded stream URL:", decodedUrl);

        // Launch video player with decoded URL
        setCurrentStreamUrl(decodedUrl);
        setCurrentStreamTitle(item.name || item.title || "Live TV");
        setCurrentStreamPoster(item.posterPath || item.logoPath || item.logo);
        setPlayerOpen(true);
      } catch (error) {
        console.error("Error decoding stream URL:", error);
        debugLog("Using original URL:", item.streamUrl);
      }
    } else if (item.mediaType === "movie" || item.type === "movie") {
      // Use mediaId, tmdbId, or id - skip if -1 or invalid
      const movieId =
        item.mediaId && item.mediaId !== -1
          ? item.mediaId
          : item.tmdbId || item.id;

      if (movieId && movieId !== -1) {
        navigate(`/web-player/movie/${movieId}`, {
          state: { playlist, profile, movie: item },
        });
      }
    } else if (
      item.mediaType === "series" ||
      item.mediaType === "tv" ||
      item.type === "series"
    ) {
      // Use mediaId, tmdbId, or id - skip if -1 or invalid
      const seriesId =
        item.mediaId && item.mediaId !== -1
          ? item.mediaId
          : item.tmdbId || item.id;

      if (seriesId && seriesId !== -1) {
        navigate(`/web-player/series/${seriesId}`, {
          state: { playlist, profile, series: item },
        });
      }
    }
  };

  return (
    <div
      className="relative min-h-screen"
      style={{
        background:
          "linear-gradient(180deg, #1c1c2a 0%, #0e0e18 46%, #030305 100%)",
      }}
    >
      {/* Dashboard Button - Bottom Right */}
      <button
        className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
        onClick={() => {
          // Clear all session data
          webPlayerStorage.clearAll();
          // Redirect to dashboard
          window.location.href = "https://dashboard.lunova.app";
        }}
      >
        Dashboard
      </button>

      {/* Left Menu */}
      <LeftMenu activeMenuItem="search" playlist={playlist} profile={profile} />

      {/* Mobile Menu */}
      <MobileMenu />
      <LeftShadow className="fixed inset-0 z-0" />

      {/* Main Content */}
      <div className="relative z-10 min-h-screen min-w-0 pb-16 pl-20 pr-4 pt-6 lg:pl-[calc(4rem+30px)] lg:pr-8 xl:pl-[calc(5rem+30px)]">
        {!resultsChrome && (
          <div className="mt-5 flex justify-center">
            <div className="flex h-12 w-[80%] items-center rounded-lg border border-[#1A1A1A] bg-white/[0.08] px-4">
              <i className="fas fa-search mr-2.5 text-sm text-white" />
              <input
                className="h-full w-full border-0 bg-transparent text-sm text-white shadow-none outline-none ring-0 placeholder:text-[#A1A1A1] focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none"
                placeholder={inputFocused ? "" : "Global Search..."}
                ref={searchInputRef}
                style={{
                  textTransform: "uppercase",
                  outline: "none",
                  boxShadow: "none",
                  border: "none",
                }}
                type="text"
                value={searchQuery}
                onBlur={() => setInputFocused(false)}
                onChange={handleSearchInput}
                onFocus={() => setInputFocused(true)}
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>
        )}

        {resultsChrome && (
          <div className="mb-3 mt-2.5 flex min-w-0 flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <div className="flex shrink-0 items-baseline gap-1.5 pl-2">
              <h1 className="text-2xl font-bold text-white">Search</h1>
              <span className="text-sm text-[#A1A1A1]">
                {activeFilter === "all"
                  ? `(${totalItems} Results)`
                  : `(${filteredResults.length} of ${totalItems} Results)`}
              </span>
            </div>
            <div className="flex min-w-0 gap-2.5 overflow-x-auto sm:flex-1 sm:justify-end">
              <button
                className="flex h-12 shrink-0 items-center rounded-lg border border-[#1A1A1A] bg-white/[0.08] px-2.5 text-sm text-white"
                type="button"
                onClick={clearSearch}
              >
                <i className="fas fa-search mr-2 text-sm" />
                <span className="max-w-[120px] truncate">{searchQuery}</span>
                <span className="ml-2 text-[#A1A1A1]">Clear Search</span>
              </button>
              {SEARCH_FILTERS.filter(
                (filter) => filter.id !== "live-tv" || signedIn
              ).map((filter) => (
                <button
                  className={pillClass(activeFilter === filter.id)}
                  key={filter.id}
                  type="button"
                  onClick={() => selectFilter(filter.id)}
                >
                  <i className={`fas ${filter.icon} mr-2 text-sm`} />
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {searchHistory.length > 0 && !searchQuery.trim() && !resultsChrome && (
          <div className="mt-1.5 flex flex-col items-center px-3 py-1.5">
            <button
              className="flex h-12 items-center rounded-lg border border-[#1A1A1A] bg-white/[0.08] px-2.5 text-sm text-white"
              type="button"
              onClick={clearAllSearchHistory}
            >
              Clear All
            </button>
            <div className="mx-auto mt-2.5 max-w-full overflow-x-auto">
              <div className="flex w-max gap-2.5 px-4">
                {searchHistory.map((item, index) => {
                  const isCast =
                    item.mediaType === "cast" || item.type === "cast";
                  const image = getItemImage(item);

                  if (isCast) {
                    return (
                      <button
                        className="group flex w-28 shrink-0 flex-col items-center px-1 text-left"
                        key={`${item.id}-cast-${index}`}
                        type="button"
                        onClick={() => handleHistoryItemClick(item)}
                        onMouseDown={() => handleHistoryLongPressStart(item)}
                        onMouseLeave={handleHistoryLongPressEnd}
                        onMouseUp={handleHistoryLongPressEnd}
                        onTouchEnd={handleHistoryLongPressEnd}
                        onTouchStart={() => handleHistoryLongPressStart(item)}
                      >
                        <img
                          alt=""
                          className="h-16 w-16 rounded-full object-cover grayscale transition duration-200 group-hover:grayscale-0 group-focus-visible:grayscale-0"
                          src={image || ""}
                        />
                        <span className="mt-1.5 w-full truncate text-center text-[11px] font-medium text-white">
                          {textOf(item.name)}
                        </span>
                      </button>
                    );
                  }

                  return (
                    <button
                      className="group flex h-24 w-[166px] shrink-0 overflow-hidden rounded-[14px] border border-[#222633] bg-[#1b1c22] text-left"
                      key={`${item.id}-${item.mediaType}-${index}`}
                      type="button"
                      onClick={() => handleHistoryItemClick(item)}
                      onMouseDown={() => handleHistoryLongPressStart(item)}
                      onMouseLeave={handleHistoryLongPressEnd}
                      onMouseUp={handleHistoryLongPressEnd}
                      onTouchEnd={handleHistoryLongPressEnd}
                      onTouchStart={() => handleHistoryLongPressStart(item)}
                    >
                      <div className="h-full w-[60px] shrink-0 overflow-hidden bg-[#111]">
                        {image ? (
                          <img
                            alt=""
                            className="h-full w-full object-cover grayscale transition duration-200 group-hover:grayscale-0 group-focus-visible:grayscale-0"
                            src={image}
                          />
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1 px-2 py-2">
                        <p className="line-clamp-2 text-[11px] font-medium text-white">
                          {textOf(item.name)}
                        </p>
                        <p className="mt-1 truncate text-[9px] text-[#C0C0C0]">
                          {getItemTypeLabel(item)}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {!resultsChrome &&
          !isLoading &&
          searchHistory.length === 0 &&
          !searchQuery.trim() && (
            <p className="px-6 py-6 text-center text-lg text-[#808080]">
              {randomMessage}
            </p>
          )}

        {searchSubmitted &&
          !isLoading &&
          filteredResults.length === 0 &&
          searchQuery.trim() && (
            <p className="px-6 py-6 text-center text-lg text-[#808080]">
              {randomMessage}
            </p>
          )}

        {isLoading && filteredResults.length === 0 && (
          <div className="flex justify-center py-10">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#6495ED]" />
          </div>
        )}

        {resultsChrome && filteredResults.length > 0 && (
          <div className="grid grid-cols-2 gap-x-2 gap-y-4 pb-16 pt-8 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {filteredResults.map((item, index) => {
              const mediaType = item.mediaType || item.type;
              const isCast = mediaType === "cast";
              const isLive =
                mediaType === "live-tv" ||
                mediaType === "livetv" ||
                item.type === "live-tv";
              const programmeTitle = textOf(item.programmes?.[0]?.title);
              const name = textOf(item.name || item.title);
              const cardTitle =
                isLive && programmeTitle ? `${name} - ${programmeTitle}` : name;
              const image = isLive
                ? item.programmes?.[0]?.backdropPath ||
                  item.backdropPath ||
                  item.logoPath ||
                  item.posterPath
                : item.posterPath || item.backdropPath || item.logoPath;
              const typeLabel =
                mediaType === "movie"
                  ? "Movie"
                  : mediaType === "series" || mediaType === "tv"
                    ? "Series"
                    : isLive
                      ? "Live TV"
                      : "";
              const rating = textOf(item.rating);

              if (isCast) {
                const movies = item.hlsStreamUrl || item.movieCount || "0";
                const series = item.tsStreamUrl || item.seriesCount || "0";

                return (
                  <button
                    className="flex flex-col items-center px-2 text-left"
                    key={`${item.mediaId || item.id}-cast-${index}`}
                    type="button"
                    onClick={() => handleContentClick(item)}
                  >
                    <img
                      alt=""
                      className="h-[130px] w-[130px] rounded-full object-cover"
                      src={item.posterPath || item.profilePath || ""}
                    />
                    <span className="mt-2.5 w-full truncate text-center text-xs font-medium text-white">
                      {name}
                    </span>
                    <span className="mt-1 w-full truncate text-center text-[10px] text-[#A1A1A1]">
                      Movies: {movies} Series: {series}
                    </span>
                  </button>
                );
              }

              return (
                <button
                  className="lunova-card-host px-2 text-left"
                  key={`${item.mediaId || item.id}-${mediaType}-${index}`}
                  type="button"
                  onClick={() => handleContentClick(item)}
                >
                  <div
                    className="lunova-card is-fixed-radius rounded-[20px]"
                    style={{
                      aspectRatio: "100 / 62",
                      background:
                        "linear-gradient(180deg, #30323E 0%, #1E1F2A 100%)",
                    }}
                  >
                    <div className="lunova-card-clip relative">
                      {image ? (
                        <img
                          alt=""
                          className={`h-full w-full ${
                            isLive && !programmeTitle
                              ? "object-contain p-3"
                              : "object-cover"
                          }`}
                          src={image}
                        />
                      ) : null}
                      {item.isBookmarked && (
                        <i className="fas fa-star absolute bottom-1.5 right-1.5 text-xs text-[#FFD700]" />
                      )}
                      {(item.watched || item.isWatched) && (
                        <i className="fas fa-circle-check absolute right-1 top-1 text-base text-[#6495ED]" />
                      )}
                    </div>
                  </div>
                  <p className="mt-2.5 truncate text-xs font-medium text-white">
                    {cardTitle}
                  </p>
                  <p className="mt-1.5 truncate text-[10px] text-[#A1A1A1]">
                    {[!isLive && textOf(item.year), typeLabel, rating]
                      .filter(Boolean)
                      .join(" - ")}
                  </p>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Search History Item Overlay */}
      <AnimatePresence>
        {showHistoryOverlay && selectedHistoryItem && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => setShowHistoryOverlay(false)}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0"
              style={{
                backdropFilter: "blur(20px)",
                backgroundColor: "rgba(0, 0, 0, 0.8)",
              }}
            />

            {/* Modal Content */}
            <motion.div
              animate={{ scale: 1, opacity: 1 }}
              className="relative z-10 w-full max-w-lg bg-gray-900/95 rounded-2xl overflow-hidden"
              exit={{ scale: 0.9, opacity: 0 }}
              initial={{ scale: 0.9, opacity: 0 }}
              style={{
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with Poster and Info */}
              <div className="flex items-center gap-4 p-6">
                {/* Poster/Logo */}
                <div
                  className={`flex-shrink-0 ${
                    selectedHistoryItem.mediaType === "live-tv" ||
                    selectedHistoryItem.mediaType === "livetv"
                      ? "w-24 h-16"
                      : "w-20 h-28"
                  } rounded-xl overflow-hidden bg-gray-800 flex items-center justify-center`}
                >
                  {getItemImage(selectedHistoryItem) ? (
                    <img
                      alt={selectedHistoryItem.name}
                      className={`${
                        selectedHistoryItem.mediaType === "live-tv" ||
                        selectedHistoryItem.mediaType === "livetv"
                          ? "w-full h-full object-contain p-2"
                          : "w-full h-full object-cover"
                      }`}
                      src={getItemImage(selectedHistoryItem)}
                    />
                  ) : (
                    <i className="fas fa-film text-gray-500 text-3xl" />
                  )}
                </div>

                {/* Title and Info */}
                <div className="flex-1">
                  <h3 className="text-white text-xl font-semibold">
                    {selectedHistoryItem.name}
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    {selectedHistoryItem.mediaType === "live-tv" ||
                    selectedHistoryItem.mediaType === "livetv"
                      ? "Live TV"
                      : selectedHistoryItem.mediaType === "movie"
                        ? "Movie"
                        : "Series"}
                  </p>
                  {/* EPG Info for Live TV */}
                  {(selectedHistoryItem.mediaType === "live-tv" ||
                    selectedHistoryItem.mediaType === "livetv") &&
                    selectedHistoryItem.programmes?.[0]?.title && (
                      <p className="text-gray-500 text-xs mt-1 truncate">
                        Now:{" "}
                        {typeof selectedHistoryItem.programmes[0].title ===
                        "object"
                          ? selectedHistoryItem.programmes[0].title?.value ||
                            selectedHistoryItem.programmes[0].title?.text
                          : selectedHistoryItem.programmes[0].title}
                      </p>
                    )}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gray-700/50 mx-6" />

              {/* Actions */}
              <div className="p-4">
                {/* Play Now - for Live TV */}
                {(selectedHistoryItem.mediaType === "live-tv" ||
                  selectedHistoryItem.mediaType === "livetv") &&
                  selectedHistoryItem.streamUrl && (
                    <>
                      <button
                        className="w-full flex items-center gap-4 px-4 py-3 text-white hover:bg-gray-800/50 rounded-xl transition-colors"
                        onClick={() => handleOverlayAction("play")}
                      >
                        <Play className="w-5 h-5 text-blue-400" />
                        <span>Play Now</span>
                      </button>

                      {/* Divider */}
                      <div className="h-px bg-gray-700/50 my-2" />

                      {/* Add/Remove from Favorites for Live TV */}
                      <button
                        className="w-full flex items-center gap-4 px-4 py-3 text-white hover:bg-gray-800/50 rounded-xl transition-colors"
                        onClick={() => handleOverlayAction("watchlist")}
                      >
                        <Bookmark
                          className={`w-5 h-5 ${isItemBookmarked ? "text-yellow-400 fill-yellow-400" : "text-blue-400"}`}
                        />
                        <span>
                          {isItemBookmarked
                            ? "Remove from Favorites"
                            : "Add to Favorites"}
                        </span>
                      </button>
                    </>
                  )}

                {/* Go to Movie/Series */}
                {(selectedHistoryItem.mediaType === "movie" ||
                  selectedHistoryItem.mediaType === "series" ||
                  selectedHistoryItem.mediaType === "tv") && (
                  <>
                    <button
                      className="w-full flex items-center gap-4 px-4 py-3 text-white hover:bg-gray-800/50 rounded-xl transition-colors"
                      onClick={() => handleOverlayAction("goto")}
                    >
                      <ExternalLink className="w-5 h-5 text-blue-400" />
                      <span>
                        Go to{" "}
                        {selectedHistoryItem.mediaType === "movie"
                          ? "Movie"
                          : "Series"}
                      </span>
                    </button>

                    {/* Divider */}
                    <div className="h-px bg-gray-700/50 my-2" />

                    {/* Add/Remove from Favorites */}
                    <button
                      className="w-full flex items-center gap-4 px-4 py-3 text-white hover:bg-gray-800/50 rounded-xl transition-colors"
                      onClick={() => handleOverlayAction("watchlist")}
                    >
                      <Bookmark
                        className={`w-5 h-5 ${isItemBookmarked ? "text-yellow-400 fill-yellow-400" : "text-blue-400"}`}
                      />
                      <span>
                        {isItemBookmarked
                          ? "Remove from Favorites"
                          : "Add to Favorites"}
                      </span>
                    </button>
                  </>
                )}

                {/* Divider */}
                <div className="h-px bg-gray-700/50 my-2" />

                {/* Remove from History */}
                <button
                  className="w-full flex items-center gap-4 px-4 py-3 text-red-400 hover:bg-red-900/20 rounded-xl transition-colors"
                  onClick={() => handleOverlayAction("remove")}
                >
                  <Trash2 className="w-5 h-5" />
                  <span>Remove</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Player Modal for Live TV */}
      {playerOpen && currentStreamUrl && (
        <PlyrVideoPlayer
          isOpen={playerOpen}
          poster={currentStreamPoster}
          streamUrl={currentStreamUrl}
          title={currentStreamTitle}
          onClose={() => {
            setPlayerOpen(false);
            setCurrentStreamUrl(null);
            setCurrentStreamTitle(null);
            setCurrentStreamPoster(null);
          }}
        />
      )}
    </div>
  );
};

export default WebPlayerSearch;
